import React from 'react';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { graphql } from 'react-relay';
import classes from './BlogPost.qmee.module.scss';
import { BlogPostQuery } from './__generated__/BlogPostQuery.graphql';
import Txt from 'components/atoms/Txt/Txt';
import Card from 'components/atoms/Card/Card';
import PageHeader from 'components/molecules/PageHeader/PageHeader';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Image from 'components/atoms/Image/Image';
import PageMetaData from 'components/atoms/PageMetaData/PageMetaData';
import ShareIcon from 'components/assets/Qmee-share.svg';
import { stripLeadingFeaturedImage } from './BlogPost.qmee.utils';

export interface Props {
  blogPostSlug?: string;
}

export const blogPostQuery = graphql`
  query BlogPostQuery($input: BlogPostQueryInput!) {
    blogPost(input: $input) {
      id
      title
      imageUrl
      content
      description
      date
    }
  }
`;

const BlogPost: React.FC<Props> = (props) => {
  const router = useRouter();
  const blogPostSlug = props.blogPostSlug || router.query.blogPostSlug;

  const { blogPost } = useLazyLoadQuery<BlogPostQuery>(
    blogPostQuery,
    { input: { slug: Array.isArray(blogPostSlug) ? blogPostSlug[0] : blogPostSlug || 'missing' } },
    {
      fetchPolicy: 'store-or-network',
    },
  );
  return blogPost ? (
    <div className={classes.root}>
      <Head>
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Qmee Blog RSS Feed"
          href={`https://qmee.com/rss/blog-rss/feed-${router.locale}.xml`}
        />
      </Head>

      <PageMetaData
        metaData={{
          title: blogPost.title,
          description: blogPost.description,
          imageUrl: blogPost.imageUrl || undefined,
          isTranslated: true,
        }}
        locales={['en-us', 'en-gb', 'en-gb', 'en-au']}
      />

      <PageHeader title="Blog Article" backHref="/blog" />

      <Card className={classes.card}>
        <h1 className={classes.title}>{blogPost.title}</h1>
        <div className={classes.meta}>
          {blogPost.date && (
            <span className={classes.date}>
              {new Date(blogPost.date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </span>
          )}
          <button
            className={classes.shareButton}
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: blogPost.title, url: window.location.href });
              } else {
                navigator.clipboard.writeText(window.location.href);
              }
            }}
          >
            <Txt skipTranslation>Share</Txt>
            <ShareIcon className={classes.shareIcon} />
          </button>
        </div>
        {blogPost.imageUrl && (
          <div className={classes.headerImage}>
            <Image
              className={classes.headerImg}
              priority
              src={blogPost.imageUrl}
              alt={blogPost.title}
              quality={100}
              width={750}
              height={415}
            />
          </div>
        )}
        <div className={classes.content}>
          <Txt
            asMarkdown
            skipTranslation
            markdownComponents={{
              img: ({ src, alt }: React.ComponentProps<'img'>) => (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={src} alt={alt} className={classes.contentImg} />
              ),
            }}
          >
            {stripLeadingFeaturedImage(blogPost.content, blogPost.imageUrl)}
          </Txt>
        </div>
      </Card>
    </div>
  ) : (
    <></>
  );
};

export default BlogPost;
